import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { IoAdd, IoTrash } from "react-icons/io5";
import { Row, Col } from "react-bootstrap";
import { sortBy } from "lodash";
import DropdownSearchSelect from "./timesheet/DropdownSearchSelect";
import "./call.scss";

import * as serviceActions from "../../actions/serviceActions";

import CustomInput from "../common/Input";
import CustomNumberInput from "../common/NumberInput";

const TasksServiceContainer = ({
  serviceId,
  service,
  actions,
  onChange,
  call,
  disabled,
  adding,
  tasks,
  services
}) => {
  const [tasksAdded, setTasksAdded] = useState(tasks || []);

  const tasksAvailable = useMemo(() => {
    const taskOptions = service?.tasks?.map(({ task, id }) => ({
      value: id,
      label: task.description
    }));
    return sortBy(taskOptions, "label");
  }, [service]);

  useEffect(() => {
    if (!services.length) actions.loadServices();
  }, [services, actions]);

  useEffect(() => {
    if (tasks.length) {
      setTasksAdded(tasks);
    }
  }, [tasks]);

  useEffect(() => {
    if (
      call?.tasks?.length &&
      !tasksAdded.length &&
      tasksAvailable.length > 0
    ) {
      const newTask = call.tasks.map(
        ({
          service_task,
          actual_duration,
          duration_units,
          external_pm_number
        }) => ({
          service_task: service_task?._id,
          duration_units,
          actual_duration,
          service: serviceId,
          external_pm_number
        })
      );
      setTasksAdded(newTask);
      onChange("tasks", newTask);
    }
  }, [
    call,
    onChange,
    service,
    service.tasks,
    serviceId,
    tasksAdded.length,
    tasksAvailable.length
  ]);

  useEffect(() => {
    if ((serviceId && !service?.id) || service.id !== serviceId) {
      actions.getService(serviceId);
    }
  }, [actions, onChange, service, serviceId]);

  useEffect(() => {
    if (adding && !tasks.length) {
      setTasksAdded([
        {
          service_task: "",
          actual_duration: 0,
          service: serviceId,
          duration_units: "Hours",
          external_pm_number: ""
        }
      ]);
      onChange("tasks", [
        {
          service_task: "",
          actual_duration: 0,
          service: serviceId,
          duration_units: "Hours",
          external_pm_number: ""
        }
      ]);
    }
  }, [adding, onChange, serviceId, tasks.length]);

  const handleChange = (name, value, i) => {
    if (name === "duration_units") {
      setTasksAdded((prev) => {
        const newPrev = prev.map((item, prevI) =>
          prevI === i ? { ...item, [name]: value, actual_duration: 0 } : item
        );
        onChange("tasks", newPrev);
        return newPrev;
      });
    } else {
      setTasksAdded((prev) => {
        const newPrev = prev.map((item, prevI) =>
          prevI === i ? { ...item, [name]: value } : item
        );
        onChange("tasks", newPrev);
        return newPrev;
      });
    }
  };

  return (
    <div className="tasks-service-container">
      {tasksAvailable.length > 0 ? (
        <>
          {tasksAdded.map((task, i) => (
            <Row key={i}>
              <Col xs={5}>
                <DropdownSearchSelect
                  disabled={disabled}
                  name="service_task"
                  label="Task"
                  onChange={(name, { value }) => handleChange(name, value, i)}
                  data={tasksAvailable}
                  value={task.service_task}
                />
              </Col>
              <Col xs={2}>
                <DropdownSearchSelect
                  disabled={disabled}
                  name="duration_units"
                  label="Units"
                  onChange={(name, { value }) => handleChange(name, value, i)}
                  data={[
                    { value: "Hours", label: "Hours" },
                    { value: "Minutes", label: "Minutes" }
                  ]}
                  value={task.duration_units}
                />
              </Col>
              <Col xs={2} style={{ marginTop: 8 }}>
                <CustomNumberInput
                  disabled={disabled}
                  name="actual_duration"
                  label="Duration"
                  onChange={(name, value) => handleChange(name, value, i)}
                  value={task.actual_duration?.toString()}
                  step="0.5"
                  pattern="[0-9]*"
                  validations={{
                    validMinutes(values, value) {
                      if (task.duration_units === "Minutes") {
                        return value % 5 === 0;
                      }
                      return true;
                    },
                    validHours(values, value) {
                      if (task.duration_units === "Hours") {
                        return 2 * value === Math.round(2 * value);
                      }
                      return true;
                    },
                    isPositive(values, value) {
                      return value >= 0;
                    }
                  }}
                  validationErrors={{
                    validMinutes: "Please use increments of 5 minutes",
                    isPositive: "Please enter a positive number",
                    validHours:
                      "Please use half hour increments or select Minutes"
                  }}
                />
              </Col>
              <Col xs={2} style={{ marginTop: 8 }}>
                <CustomInput
                  disabled={disabled}
                  name="external_pm_number"
                  label="Ext. Task ID"
                  value={task.external_pm_number}
                  onChange={(name, value) => handleChange(name, value, i)}
                />
              </Col>
              <Col className="timesheet" xs={1}>
                <button
                  className="btn btn-transparent btn-delete"
                  disabled={disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setTasksAdded((prev) => {
                      const newPrev = prev.filter(
                        (item, prevId) => prevId !== i
                      );
                      onChange("tasks", newPrev);
                      return newPrev;
                    });
                  }}
                >
                  <IoTrash size={18} />
                </button>
              </Col>
            </Row>
          ))}
          <Row
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: "1rem"
            }}
          >
            <button
              className="btn btn-blue"
              style={{
                display: "flex"
              }}
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setTasksAdded((prev) => {
                  const newValues = [
                    ...prev,
                    {
                      service_task: "",
                      actual_duration: 0,
                      service: serviceId,
                      duration_units: "Hours"
                    }
                  ];
                  onChange("tasks", newValues);
                  return newValues;
                });
              }}
            >
              <IoAdd size={18} /> Add Task
            </button>
          </Row>
        </>
      ) : null}
    </div>
  );
};
TasksServiceContainer.propTypes = {
  actions: PropTypes.object.isRequired,
  serviceId: PropTypes.string,
  onChange: PropTypes.func,
  service: PropTypes.object,
  services: PropTypes.array,
  tasks: PropTypes.array,
  call: PropTypes.object,
  disabled: PropTypes.bool,
  adding: PropTypes.bool
};
TasksServiceContainer.defaultProps = {
  disabled: false,
  adding: false,
  tasks: []
};

function mapStatesToProps(state) {
  return {
    state: state.reducers,
    auth: state.reducers.auth,
    call: state.reducers.call,
    savingUser: state.reducers.savingUser,
    user: state.reducers.user,
    users: state.reducers.users,
    departments: state.reducers.departments,
    roles: state.reducers.roles,
    service: state.reducers.service,
    services: state.reducers.services
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...serviceActions
      },
      dispatch
    )
  };
}

export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(TasksServiceContainer);
